import Vue from 'vue';
import { AxiosError } from 'axios';
import { Component, Watch } from 'vue-property-decorator';
import uniqBy from 'lodash/uniqBy';
import flatMap from 'lodash/flatMap';
import moment from 'moment';
import {
  BagClickEvent, CheckoutEvent,
  ErrorEvent,
  SubmitOrderEvent,
} from '@/services/analytics/events';
import { getEntreeCount } from './cmg-checkout-helpers';

import {
  OrderWithContext,
  Restaurant,
  SubmitOrderRequestPaymentMethodType,
  OrderApiException,
  CustomerProfile,
  DeliveryEstimate,
  UpdateDeliveryInfoOnOrderRequest,
  Address,
  TokenizedPaymentCard,
  UndeliverableItem,
  CharitableCampaign,
  BagPickupLocation,
  OrderErrorCode,
  SubmitOrderRequestPaymentMethod,
  Order,
} from '@/models';

import { Routes } from '@/router';
import { ui, PaymentProviders } from '@/constants';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { FooterMode, CountryCode } from '@/store/ui';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { PickupDeliveryToggleMode } from '@/components/CmgPickupDeliveryToggle/cmg-pickup-delivery-toggle';
import { displayOrderSubmitErrorModal, RequestData } from './cmg-checkout-helpers';
import { OrderAndHistoryMetricsRequest } from '@/models/sustainability';
import { getCurrencyCode, getStartOfYear } from '@/helpers';
import { cloneDeep } from 'lodash';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { sendMessageToChatIframe } from '@/scripts/amelia-pepper-integration';
import { loadApplePaySdk } from '@/scripts/loadApplePay';
import { loadGooglePaySDK } from '@/scripts/loadGooglePay';

declare global {
  interface Window {
    ApplePaySession: typeof ApplePaySession;
  }
}

@Component({
  name: 'CmgCheckout',
})
export default class CmgCheckout extends Vue {
  isRemovingUndeliverableItems = false;
  isFetchingPaymentProvider = true;
  charitableCampaign: CharitableCampaign | null = null;
  promotionRemoved = false;
  bagPickupLocation: BagPickupLocation | null = null;
  isCustomTip = false;
  isRemovingCrewTip = false;
  isProcessingError = false;
  processingErrorText = '';
  isApplePaySupported = false;
  interstitialResolver: ((value: boolean) => void) | null = null;
  unsubscribe: (() => void) | null = null;
  interstitialErrorOccurred = false;
  available = false;
  isGooglePayLoading = true;

  get isSubmittingOrder(): boolean {
    return this.$appStore.state.checkout.isSubmittingOrder;
  }

  get isUpdatedContextualRewardsBagUpdatesEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isUpdatedContextualRewardsBagUpdatesEnabled;
  }

  get order(): OrderWithContext | null {
    return this.$services.order.getCurrentOrder();
  }

  get restaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isPickup(): boolean {
    return this.$services.order.isPickingUp();
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get isGuest(): boolean {
    if (!this.customer) {
      return true;
    }

    return this.customer.isGuest;
  }

  get deliveryAddressName(): string {
    // Added defensive guard in case state.order is undefined in test/mocked environment
    const orderState = this.$appStore.state.order;
    if (!orderState) {
      return '';
    }
    const delivery = orderState.selectedDeliveryAddress;
    return (delivery && delivery.name) || '';
  }

  get address(): Address | null {
    // Added guard for missing state.order to avoid TypeError when mocked tests omit it
    if (!this.order || !this.restaurant || !this.$appStore.state.order) {
      return null;
    }

    const delivery = this.$appStore.state.order?.selectedDeliveryAddress;
    const restaurantAddress = this.restaurant.address;

    return delivery
      ? {
        line1: delivery.streetAddress1 || '',
        line2: delivery.streetAddress2 || '',
        postalCode: delivery.postalCode || '',
        locality: delivery.city || '',
        administrativeArea: delivery.state || '',
        latitude: delivery.latitude,
        longitude: delivery.longitude,
      }
      : {
        line1: restaurantAddress.addressLine1 || '',
        postalCode: restaurantAddress.postalCode || '',
        latitude: restaurantAddress.latitude,
        longitude: restaurantAddress.longitude,
        countryCode: restaurantAddress.countryCode || '',
        locality: restaurantAddress.locality || '',
        administrativeArea: restaurantAddress.administrativeArea || '',
      };
  }

  get deliveryEstimate(): DeliveryEstimate | null {
    return this.$appStore.state.checkout.delivery.estimate;
  }

  get isModalOpen(): boolean {
    return this.$appStore.state.ui.modals.deliveryAddress.visible;
  }

  get minimumOrderTotalForDelivery(): number | null {
    return this.order && this.order.order.delivery && this.order.order.delivery.minimumOrderValue;
  }

  get orderSubtotal(): number | null {
    return this.order && this.order.order.orderMealsSubTotalAmount;
  }

  get isEligibleForDelivery(): boolean {
    const isIneligible = !!this.order && this.minimumOrderTotalForDelivery !== null && this.order.order.orderMealsSubTotalAmount < this.minimumOrderTotalForDelivery;
    if (isIneligible) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.Delivery,
        action: AnalyticsEventAction.ErrorMessage,
        label: AnalyticsEventLabel.BelowMinimumOrder,
      });
      this.$services.analytics.send(new ErrorEvent('checkout-delivery', `Sorry, minimum order total for delivery is $10.00`, 'checkout'));
    }
    return !isIneligible;
  }

  get undeliverableItems(): UndeliverableItem[] {
    const menu = this.$appStore.state.menu.activeMenu;
    const menuItemsLookup = this.$appStore.state.menu.menuItemLookupMap;
    if (!this.order || !menu || !menuItemsLookup) {
      return [];
    }

    const undeliverableItems = (this.order.order.meals || []).reduce((allItems: UndeliverableItem[], meal) => {
      const contentIds = flatMap(meal.entrees, entree => (entree.contents || []).map(content => content.menuItemId));

      const ineligibleItems = (meal.entrees || [])
        .map(e => e.entreeId)
        .concat((meal.sides || []).concat(meal.drinks || []).map(i => i.menuItemId))
        .concat(contentIds)
        .reduce((mealItems, id) => {
          const item = menuItemsLookup.get(id);

          if (item && !item.isEligibleForDelivery) {
            mealItems.push({ id, name: item.menuItemName });
          }

          return mealItems;
        }, [] as UndeliverableItem[]);

      allItems.push(...ineligibleItems);
      return allItems;
    }, []);

    if (undeliverableItems.length > 0) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.Delivery,
        action: AnalyticsEventAction.ErrorMessage,
        label: AnalyticsEventLabel.ItemsCantDeliver,
      });
    }

    return undeliverableItems;
  }

  get undeliverableItemNames(): string[] {
    return uniqBy(this.undeliverableItems, i => i.id).map(i => i.name);
  }

  get isValidOrder(): boolean {
    return !!this.order && (this.isPickup || (!this.isPickup && this.isEligibleForDelivery && !this.undeliverableItems.length));
  }

  get isFreeOrder(): boolean {
    return this.order?.order.orderTotalAmount === 0;
  }

  get freeOrderHeaderText(): string {
    return `YOUR ORDER TOTAL IS ${this.formatPrice(0)}!`;
  }

  get dancingAvocadoVideoUrl() {
    return this.$appStore.state.cms.images.dancingAvocado.default;
  }

  get isAboveStorePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus) {
      if (!flags.orderingStatus.aboveStorePaymentOnline) {
        return false;
      }

      if (this.isUCom && !this.isUComPaymentOnline) {
        return false;
      }

      if (this.isPayeezy && !this.isPayeezyPaymentOnline) {
        return false;
      }

      if (this.isChase && !this.isChasePaymentOnline) {
        return false;
      }

      if (this.isChaseUK && !this.isChaseUKPaymentOnline) {
        return false;
      }
    }

    return true;
  }

  get isPayeezyPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isPayeezyPaymentOnline) {
      return false;
    }

    return true;
  }

  get isChasePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isChasePaymentOnline) {
      return false;
    }

    return true;
  }

  get isChaseUKPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isChaseUKPaymentOnline) {
      return false;
    }

    return true;
  }

  get isUComPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isUComPaymentOnline) {
      return false;
    }

    return true;
  }

  get isPayeezy(): boolean {
    return this.$appStore.state.checkout.paymentProvider == PaymentProviders.Payeezy;
  }

  get isChase(): boolean {
    return this.$appStore.state.checkout.paymentProvider == PaymentProviders.Chase;
  }

  get isChaseUK(): boolean {
    return this.$appStore.state.checkout.paymentProvider === PaymentProviders.ChaseUK;
  }

  get isUCom(): boolean {
    return this.$appStore.state.checkout.paymentProvider == PaymentProviders.UCom;
  }

  get willPayAboveStore(): boolean {
    return this.isAboveStorePaymentOnline && !this.$appStore.state.checkout.payment.forceInStorePayment;
  }

  get payInStoreAddressLine1(): string {
    return this.restaurant ? this.restaurant.address.addressLine1 || '' : '';
  }

  get payInStoreAddressLine2() {
    return this.restaurant ? `${this.restaurant.address.locality || ''}, ${this.restaurant.address.administrativeArea || ''} ${this.restaurant.address.postalCode || ''}` : '';
  }

  get footerMode(): FooterMode {
    return this.isValidOrder || !this.isEligibleForDelivery ? FooterMode.Micro : FooterMode.None;
  }

  get crossBorderMessage(): string {
    // Rule for showing message: Customer Profile country is different from restaurant country
    // What to show: string, stating that payment methods shown below are for restaurant's country
    if (this.restaurant && this.$appStore.state.customer.profile && this.$appStore.state.customer.profile.country) {
      const profileCountryCode = this.$appStore.state.customer.profile.country;
      const restaurantCountryCode = this.restaurant.address.countryCode;
      if (restaurantCountryCode && profileCountryCode !== restaurantCountryCode) {
        return this.getCrossBorderMessage(restaurantCountryCode);
      }
    }
    return '';
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isOrderingOnline(): boolean {
    return this.$services.utils.isOrderingOnline();
  }

  get isDeliveryOnline(): boolean {
    return this.$services.utils.isDeliveryOnline();
  }

  get paymentType(): string {
    const payments = this.$appStore.state.order.lastOrder.order?.order.payments;
    return payments?.length ? payments[0].paymentTypeName : '';
  }

  get hasOffer(): boolean {
    return !!(this.order && this.order.discounts && this.order.discounts.length);
  }

  get isStubPayment(): boolean {
    return this.$services.config.getIsStubPayment();
  }

  get giftCard(): TokenizedPaymentCard | undefined {
    return this.$appStore.state.customer.wallet.paymentCards.find(card => card.isGiftCard === true);
  }

  get selectedPaymentMethods(): SubmitOrderRequestPaymentMethod[] {
    return this.$appStore.state.checkout.payment.methods;
  }

  get selectedGiftCardPayment(): SubmitOrderRequestPaymentMethod | undefined {
    return this.selectedPaymentMethods.find(payment => payment.giftCardNumber);
  }

  @Watch('customer', { immediate: true })
  onCustomerChanged(customer: CustomerProfile | null) {
    if (!customer) {
      return;
    }

    this.$services.transaction.refreshPaymentCards();
  }

  @Watch('restaurant', { immediate: true, deep: true })
  restaurantChanged() {
    this.bagPickupLocation = this.restaurant?.chipotlanePickupEnabled
      ? BagPickupLocation.Chipotlane
      : this.restaurant?.walkupWindowEnabled
        ? BagPickupLocation.WalkUpWindow
        : BagPickupLocation.Shelf;
    
    this.$services.ui.setAppScrollability(true);
  }

  async mounted() {
    // Must be loaded to monitor user activity and generate a session value to be passed into order submit.
    // This is used in the SEON integration, which is used in the submit api to detect and prevent fraud orders.
    this.$services.seonService.loadLibrary();
    try {
      await loadApplePaySdk();
      this.available = this.canUseApplePay();
    } catch {
      this.available = false;
    }

    try {
      await loadGooglePaySDK(); 
    } catch(error: any) {
      this.$services.logger.error('Error: Could not load Google Pay SDK', undefined, error);
    } finally {
      this.isGooglePayLoading = false;
    }

    await this.$services.config.setOrderReviewModal();

    if (this.undeliverableItemNames.length) {
      this.$services.analytics.send(new ErrorEvent('checkout', `Sorry, we can't deliver some items in your bag.`, 'undeliverable-items'));
    }
    // Check if online ordering flag was turned off in APIM
    await this.$services.featureFlags.validateOnlineOrdering();

    await this.validateOrderState();

    // Customer state is required before we can load the provider
    if (!this.$store.state.customer.profile) {
      await this.$services.customer.refreshCustomerProfileInfo();
    }

    await this.getProvider();

    if (!this.isAboveStorePaymentOnline && !this.isPickup && this.order) {
      this.showPaymentIssueModal(this.order);
    }

    if (!this.willPayAboveStore) {
      await this.removePromotionsFromOrder();
    }

    if (!this.$appStore.state.checkout.delivery.tip.value && this.order && this.order.order.orderTipAmount) {
      this.$services.checkout.setDeliveryTip(this.order.order.orderTipAmount - (this.order.order.orderCrewTipAmount || 0));
    }

    if (!this.$appStore.state.checkout.crewTip.tip.value && this.order && this.order.order.orderTipAmount) {
      this.$services.checkout.setCrewTip(this.order.order.orderTipAmount - (this.order.order.delivery?.tipAmount || 0));
    }

    // Getting charitable campaign here so we know to display so that the data will
    // be available for cmg-charitable-donation component
    // Checking to make sure donations are enabled as well. For example, donations are not available in UK
    if (this.$appStore.state.featureFlags.orderingStatus?.isDonationsEnabled && !this.$appStore.state.order.charitableCampaign) {
      const charitableCampaign = await this.$services.donations.getCharitableCampaign(this.countryCode);
      this.$services.donations.setCharitableCampaign(charitableCampaign);
    }

    // User cannot see group order participant count from checkout page, so stop unnecessary calls
    this.$services.order.stopPollingGroupOrder();
    const focusFirstelement = document.querySelectorAll('.minimum-error')[0] as HTMLElement;
    if (focusFirstelement) {
      focusFirstelement.focus();
    }

    this.getIsApplePaySupported();
    
    this.$services.ui.setAppScrollability(true);

    const entries = performance.getEntriesByType("navigation");
    entries.forEach((entry) => {
      if ((entry as PerformanceNavigationTiming).type === 'reload') {
        if (this.$appStore.state.checkout.isOrderConfirmedError) {
          this.$services.ui.setModalVisibility('basicError', true, {
            header: "AW SHUCKS",
            content: `Your order wasn\'t submitted.\n Please try again. <video autoplay loop muted class="maxw-100" src="${this.$appStore.state.cms.images.feedback.sadAvocado.default}"/>`,
            background: "white",
            isInterstitialError: true,
            options: [
              {
                text: 'BACK TO CHECKOUT',
                type: ButtonType.Primary,
                onClick: () => { 
                  this.$services.ui.setModalVisibility('basicError', false);
                },
              },
            ],
          });
          // Apply analytics error details for checkout event after page refreshes
          this.$services.analytics.applyOne({ 
            commerce: { 
              orderNotSubmitted: { "value": 1 }
            },
            error: {
              "errors": {
                  "value": 1
              },
              "errorName": "checkout|AW SHUCKS. Your order wasn't submitted. Please try again.",
              "errorType": "checkout"
            },
            web: {
              "webInteraction": {
                "linkModuleName": "checkout|refreshed-browser-during-order-review",
                "linkClicks": {
                    "value": 1
                },
                "type": "other"
              },
              webPageDetails: {}
            }
          });
        }
        // Apply adobe event END
        this.$services.checkout.setIsOrderConfirmedError(false);
      }
    });
  }

  canUseApplePay(): boolean {
    return !!window.ApplePaySession &&
      window.ApplePaySession.canMakePayments();
  }

  beforeDestroy() {
    // If user navigates away from checkout page, there is a potential need to begin polling group order again
    this.$services.order.pollGroupOrderIfExists();

    if (this.unsubscribe) {
      this.unsubscribe();
    }

    if (this.$appStore.state.ui.modals.basicError.visible || this.$appStore.state.ui.modals.toast.visible) {
      this.$services.ui.setModalVisibility('basicError', false);
      this.$services.ui.setModalVisibility('toast', false);
    }
  }

  async getIsApplePaySupported(): Promise<void> {
    if (window.ApplePaySession) {
      try {
        const merchantId = this.$services.config.getApplePayMerchantId();
        this.$services.logger.info(`Verifying if Apple Pay is supported for merchant id: ${merchantId}`);
        this.isApplePaySupported = await window.ApplePaySession.canMakePaymentsWithActiveCard(merchantId);
      } catch (error) {
        // Explicitly set to false on error so tests and UI reflect unsupported state
        this.isApplePaySupported = false;
        this.$services.logger.error('Error while verifying if Apple Pay is supported', undefined, error);
      }
    } else {
      this.$services.logger.error('Apple Pay is not supported.');
    }
  }

  async getOrderCancellationEnabled(restaurantNumber: number): Promise<boolean> {
    if( restaurantNumber ) {
      await this.$services.restaurant.refreshRestaurantExperience(restaurantNumber);
    }
    return this.$appStore.state.restaurant?.selectedRestaurant?.orderCancellationEnabled ?? false;
  }

  async onApplePayButtonClicked() {
    if (!window.ApplePaySession) {
      return;
    }

    try {
      const giftCardPayment = this.$appStore.state.checkout.payment.methods.find(m => m.paymentType === SubmitOrderRequestPaymentMethodType.ChipotleWalletGiftCard && m.amount && m.amount < (this.order?.order.orderTotalAmount || 0));
      if (giftCardPayment) {
        const filtered = [cloneDeep(giftCardPayment)]; // retain only gift card portion; ApplePay will cover remainder
        this.$services.checkout.setPaymentMethods(filtered);
      } else {
        this.$services.checkout.setPaymentMethods([]);
      }
      this.beginApplePaySession();
    } catch (e) {
      this.$services.logger.error('Failed auto-deselecting credit card before Apple Pay', {}, e);
    }
  }

  private beginApplePaySession() {
    try {
      if (!window.ApplePaySession) {
        return;
      }
    const partialPaymentAmount = this.getPartialPayment();
    const formattedPaymentAmount = partialPaymentAmount ? partialPaymentAmount.toFixed(2) : this.order?.order.orderTotalAmount.toString() || '';
    const merchantIdentifier = this.$services.config.getApplePayMerchantId();
    const countryCode = this.$appStore.state.ui.countryProfile.countryCode;

    const request: ApplePayJS.ApplePayPaymentRequest = {
      countryCode,
      currencyCode: getCurrencyCode(countryCode),
      merchantCapabilities: [
        'supports3DS'
      ],
      supportedNetworks: [
        'visa',
        'masterCard',
        'amex',
        'discover'
      ],
      total: {
        label: 'Total',
        type: 'final',
        amount: formattedPaymentAmount
      }
    };

    const session = new window.ApplePaySession(3, request);

    session.onvalidatemerchant = async event => {
      try {
        const merchantSession = await this.$services.transaction.getApplePayPaymentSession(merchantIdentifier);
        this.$services.logger.debug('Apple Pay merchant session', merchantSession);

        session.completeMerchantValidation(merchantSession);
      } catch (e: any) {
        this.$services.logger.error('Error completing Apple Pay merchant validation: ', {}, e);
      }
    };

    session.onpaymentmethodselected = event => {
      const update: ApplePayJS.ApplePayPaymentMethodUpdate = { newTotal: { label: 'Total', amount: formattedPaymentAmount } };
      session.completePaymentMethodSelection(update);
    };

    session.onpaymentauthorized = async event => {
      const paymentData = event.payment.token.paymentData;

      let paymentMethodsToBeSubmitted: SubmitOrderRequestPaymentMethod[] = [];

      // If a giftcard is a selected payment
      if (this.selectedGiftCardPayment && this.giftCard && partialPaymentAmount > 0) {
        paymentMethodsToBeSubmitted.push(cloneDeep(this.selectedGiftCardPayment));
      }

      const submitOrderRequest: SubmitOrderRequestPaymentMethod = {
        paymentType: SubmitOrderRequestPaymentMethodType.ApplePay,
        ephemeralPublicKey: paymentData.header.ephemeralPublicKey,
        ephemeralPublicKeyHash: paymentData.header.publicKeyHash,
        merchantIdentifier,
        paySignature: paymentData.signature,
        nativeWalletData: paymentData.data,
        transactionId: event.payment.token.transactionIdentifier,
        version: paymentData.version,
        amount: parseFloat(formattedPaymentAmount) || this.order?.order.orderTotalAmount,
        paymentProviderId: this.$appStore.state.checkout.paymentProvider
      };

      paymentMethodsToBeSubmitted.push(submitOrderRequest);

      this.$services.checkout.setPaymentMethods(paymentMethodsToBeSubmitted);

      let status = window.ApplePaySession.STATUS_SUCCESS;
      try {
        let orderCancellationEnabled = await this.getOrderCancellationEnabled(this.restaurant?.restaurantNumber || 0);
        if (orderCancellationEnabled) {
          session.completePayment({ status });
        }
        await this.onSubmitOrder();
      } catch (error) {
        status = window.ApplePaySession.STATUS_FAILURE;
        this.$services.logger.error('Failed to submit order with Apple Pay: ', submitOrderRequest, error);
      }
      session.completePayment({ status });
    };

    session.oncancel = event => {
      this.$services.logger.info('User canceled Apple Pay');
      this.$services.checkout.setPaymentMethods([]);
    };

    session.begin();
    } catch (e: any) {
      this.$services.logger.error('Error beginning Apple Pay session', {}, e);
    }
  }

  getPartialPayment(): number {
    const orderTotalAmount = this.order?.order.orderTotalAmount || 0;
    if (this.selectedGiftCardPayment && this.giftCard) {
      const giftCardBalance = this.$appStore.state.customer.wallet.giftCardBalances[this.giftCard.tokenizedAccountNumber];
      if (giftCardBalance < orderTotalAmount) {
        return orderTotalAmount - giftCardBalance;
      }
    }
    return 0;
  }

  onPickupTimeSelected(time: string, bypassValidation: boolean) {
    this.$services.checkout.setSelectedPickupTime(time);

    // Allow for validation bypass, since on alternate pickup location selection, we need to clear out the 
    // selected time (aka an invalid time), but also don't want to force an error state message on the customer
    if (!bypassValidation) {
      this.$services.checkout.validatePickup(['pickupTime']);
    }

    if (this.order) {
      this.$services.googleAnalytics.sendEcommerceCheckoutEvent(this.order, 'select a time', 2);
    }
  }

  onSetBagPickupLocation(bagLocation: BagPickupLocation) {
    this.bagPickupLocation = bagLocation;
  }

  formatPrice(price: number | null): string {
    return this.$services.utils.formatPrice(price);
  }

  onClickViewBag(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.bag.setBagVisibility(true);
    Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
  }

  onClickAddAnotherMenuItem() {
    this.$router.push({ name: Routes.Order, hash: '#menu' });
  }

  onTogglePickupDeliveryForIneligibleOrder(mode: PickupDeliveryToggleMode) {
    if (mode == 'delivery') {
      return;
    }
    this.$services.order.switchToPickup();
  }

  onSelectTip(event: { isCustomTip: boolean; }) {
    this.isCustomTip = event.isCustomTip;
  }

  async onClickRemoveUndeliverableItems() {
    if (!this.order) {
      return;
    }

    try {
      this.isRemovingUndeliverableItems = true;

      await this.$services.order.removeItemsFromOrder(this.order, this.undeliverableItems);
    } finally {
      this.isRemovingUndeliverableItems = false;
    }
    const focusFirstelement = document.querySelectorAll('.slot-wrapper.addMenuitem')[0] as HTMLElement;
    const focusAddressheader = document.querySelectorAll('.address-header')[0] as HTMLElement;
    if (focusFirstelement) {
      focusFirstelement.focus();
    }
    if (focusAddressheader) {
      focusAddressheader.focus();
    }
  }

  private async orderReviewInterstitialEnabled() {
    let orderCancellationEnabled = await this.getOrderCancellationEnabled(this.restaurant?.restaurantNumber || 0);
    return this.countryCode !== CountryCode.GB && orderCancellationEnabled;
  }

  async onSubmitOrder() {
    this.$services.analytics.send(new BagClickEvent(InteractionTrackingType.OrderNowClicks, this.order?.order.meals));
    this.processingErrorText = '';
    this.isProcessingError = false;
    if (!this.order) {
      this.$services.logger.unreachable('Somehow order was null!');
      return;
    }

    const paymentMethod = this.$appStore.state.checkout.payment.methods;
    if (paymentMethod && paymentMethod[0]
      && paymentMethod[0].paymentProviderId === PaymentProviders.ChaseUK
      && ((paymentMethod[0].paymentType === SubmitOrderRequestPaymentMethodType.ChipotleWalletCreditCard && !paymentMethod[0].transactionId && this.order!.order.orderTotalAmount > 0)
        || (paymentMethod[0].paymentType === SubmitOrderRequestPaymentMethodType.CreditCard && paymentMethod[0].preauthorizedAmount !== this.order!.order.orderTotalAmount))) {
      this.showPaymentAuthorizationModal(paymentMethod[0], this.order!.order);
      return;
    }

    if (this.isStubPayment) {
      let stubCard: SubmitOrderRequestPaymentMethod[] = [];
      stubCard.push(this.$services.config.getStubPayment());
      this.$services.checkout.setPaymentMethods(stubCard);
    }

    // Save order in a local var as it gets overwritten before it gets to sustainability API call
    const order = this.order;

    // Set focus back element
    this.$services.ui.setLastSelectedElement('submit-order');

    // Check if delivery ordering flag was turned off in APIM
    if (!this.isPickup && !this.isDeliveryOnline) {
      this.showDeliveryOfflineModal();
      return;
    }

    // Check if online ordering flag was turned off in APIM
    await this.$services.featureFlags.refreshApimFeatureFlags();
    if (!this.isOrderingOnline) {
      this.$router.replace({ path: '/offline' });
      return;
    }

    // skip form validation if we're using chase or UCom provided form. Validation is done postMessaging
    let invalidPaymentForm = false;
    if (this.$appStore.state.checkout.paymentProvider !== PaymentProviders.Chase &&
      this.$appStore.state.checkout.paymentProvider !== PaymentProviders.UCom &&
      this.$appStore.state.checkout.paymentProvider !== PaymentProviders.ChaseUK) {
      if (this.isGuest && this.willPayAboveStore && !this.isStubPayment) {
        const paymentForm = this.$appStore.state.checkout.payment.paymentForm;
        if (!paymentForm) {
          throw new Error('Error: Invalid payment form.');
        }
        const validatePaymentFormResult = await this.$services.transaction.validatePaymentForm(paymentForm);
        invalidPaymentForm = validatePaymentFormResult.error;
      }
    }

    // Validation will add errors to any invalid fields so we just need to bail out if it returns false
    if (!this.$services.checkout.validate(this.willPayAboveStore) || invalidPaymentForm) {
      const body = 'Looks Like You Missed Something';
      const checkoutType = this.isPickup ? 'checkout-pickup' : 'checkout-delivery';
      this.$services.analytics.send(new ErrorEvent(checkoutType, body, 'invalid-form'));

      this.$services.ui.setModalVisibility('toast', true, {
        body,
        bodyOnly: true,
      });

      this.$nextTick(() => {
        this.scrollToFirstError();
      });
      return;
    }

    if (this.willPayAboveStore && !this.isAboveStorePaymentOnline) {
      this.showPaymentIssueModal(this.order);
      return;
    }

    // Display order review interstitial if it is enabled before submitting the order
    let orderCancellationEnabled = await this.orderReviewInterstitialEnabled();
    if (orderCancellationEnabled) {
      if (this.$appStore.state.checkout.payment.methods.length === 0) {
        this.$services.ui.setModalVisibility('basicError', true, {
          header: "SORRY, WE DIDN\'T QUITE GET THAT.",
          content: "Please double-check your payment information and try again.",
          background: "white",
          options: [
            {
              text: 'OKAY',
              type: ButtonType.Primary,
              onClick: () => { 
                this.$services.ui.setModalVisibility('basicError', false);
              },
            }
          ]
        })

        return;
      }
      this.$services.analytics.send(new CheckoutEvent('review-order'));
      this.$services.ui.setModalVisibility('orderReviewModal', true, {
        isPickupOrder: this.isPickup,
        address: this.address,
        restaurant: this.restaurant,
        countdownTimerValue: this.$services.config.getOrderReviewModal()?.countdownTimer || 10
      });

      document.addEventListener("visibilitychange", () => {
        if (document.hidden && this.$appStore.state.ui.modals.orderReviewModal.visible) {
          this.$services.ui.setModalVisibility('orderReviewModal', false);
          this.interstitialErrorOccurred = true;
          this.$services.ui.setModalVisibility('basicError', true, {
            header: "AW SHUCKS",
            content: `Your order wasn\'t submitted.\n Please try again. <video autoplay loop muted class="maxw-100" src="${this.$appStore.state.cms.images.feedback.sadAvocado.default}"/>`,
            background: "white",
            isInterstitialError: true,
            options: [
              {
                text: 'BACK TO CHECKOUT',
                type: ButtonType.Primary,
                onClick: () => { 
                  this.$services.ui.setModalVisibility('basicError', false);
                },
              },
            ],
          });
          // Send analytics call
          this.$services.analytics.applyOne({ 
            commerce: { 
              orderNotSubmitted: { "value": 1 }
            },
            web: {
              webPageDetails: {
                "name": "order:checkout:cancel-order-review"
              }
            }
          });
          this.$services.analytics.send(new ErrorEvent('checkout', `AW SHUCKS. Your order wasn't submitted. Please try again.`, 'closed-screen-during-order-review'));

          if (this.$appStore.state.ui.modals.toast.visible) {
            this.$services.ui.setModalVisibility('toast', false);
          }
        }
      }, { once: true });

      this.subscribeToIsOrderConfirmed();

      const orderConfirmed = await new Promise<boolean>((resolve) => {
        this.interstitialResolver = resolve;
      });

      if (!orderConfirmed) {
        this.$services.checkout.setIsOrderConfirmed(null);

        // Refresh pickup/delivery time estimates
        this.refreshTimeEstimates(order);

        if (!this.interstitialErrorOccurred) {
          this.$services.ui.setModalVisibility('toast', true, { 
            title: '',
            body: 'YOUR ORDER WASN\'T SUBMITTED.',
            bodyOnly: true
          });
        } else {
          this.interstitialErrorOccurred = false;
        }

        return;
      }
    }
    
    // At this point we know for sure we're going to submit the order
    this.$services.checkout.setIsSubmittingOrder(true);
    this.$services.ui.setAppBlur(true);
    this.$services.ui.setAppPepperVisibility(true);
    this.$services.checkout.setIsOrderConfirmed(null);

    try {
      // If we're not paying in store, then there are some additional things we need to do to the order:
      if (!this.willPayAboveStore) {
        await this.removePromotionsFromOrder();
      }

      // The order flows are different enough that we branch here to handle operations independently
      if (this.isGuest) {
        await this.submitGuestOrder();
      } else {
        await this.submitAuthenticatedOrder();
      }

      const seonSessionPromise = await this.$services.seonService.getSession();
      
      sendMessageToChatIframe({
        name: this.$appStore.state.customer.profile?.firstName ?? '',
        email: this.$appStore.state.customer.profile?.email ?? '',
        phoneNumber: this.$appStore.state.customer.profile?.phoneNumber ?? '',
        isGuest: this.isGuest,
        lastOrder: {
          orderShortCode: order?.order?.orderShortCode ?? '',
        },
        sessionHash: seonSessionPromise || ""
      });

      // Ensure that feedback has been cleared for any new order.
      this.$services.feedback.setFeedback(null);

      // Sustainability only available for US at the moment
      if (this.countryCode === CountryCode.US) {
        // Submit sustainability metrics
        const orderId = order.order.orderId;
        const meals = order.order.meals || [];
        const postSustainabilityMetricsBody: OrderAndHistoryMetricsRequest = {
          orderId,
          isGuest: this.isGuest,
          country: this.countryCode,
          meals,
          startDate: getStartOfYear(),
          orderSubmitDateTimeUtc: moment().utc().format() // prettier-ignore
        };

        try {
          await this.$services.sustainability.postCurrentOrderAndHistorySustainabilityMetrics(postSustainabilityMetricsBody);
        } catch (e: any) {
          this.$services.logger.error('Error POSTing sustainability metrics', postSustainabilityMetricsBody, e);
          this.$services.checkout.setSustainabilityError({ didError: true, postSustainabilityMetricsBody });
        }
      }
    } finally {
      // keep the UI in a loading state if we're resubmitting the order without a crew tip
      if (!this.isRemovingCrewTip) {
        this.$services.checkout.setIsSubmittingOrder(false);
        this.$services.ui.setAppPepperVisibility(false);
        this.$services.ui.setAppBlur(false);
      }
    }
  }

// TODO-mce365: handle errors
  private async submitAuthenticatedOrder() {
    try {
      const order = this.order!;

      // Grab the order id (since we're about to lose it after we reset the orders)
      const orderId = order.order.orderId;

      // Right before checkout, make sure group orders are up-to-date
      if (this.$services.order.isCustomerHostOfGroupOrder()) {
        await this.$services.order.refreshCurrentOrder();
      }

      try {
        await this.submitOrder();
      } catch (e: any) {
        // Throw error if payment is native wallet in order to complete wallet session with a status.
        if (this.$appStore.state.checkout.payment.methods.some(method =>
          method.paymentType === SubmitOrderRequestPaymentMethodType.ApplePay
          || method.paymentType === SubmitOrderRequestPaymentMethodType.GooglePay
        )) {
          throw e;
        }
        return;
      }

      // refetch gift card balance after submitting order
      const giftCard = this.$appStore.state.checkout.payment.methods.find(card => card.hasOwnProperty('giftCardNumber'));
      if (giftCard && giftCard.giftCardNumber) {
        this.$services.transaction.getGiftCardBalance(giftCard.giftCardNumber);
      }

      // Reset checkout state
      this.$services.order.resetCurrentOrder();
      this.$services.checkout.reset();
      this.$services.recentAndFavoriteOrders.clearFavoriteOrdersCache();
      this.$services.recentAndFavoriteOrders.clearRecentOrdersCache();

      // Take the customer to the confirmation screen
      this.$router.replace({ name: Routes.OrderConfirmation, params: { orderId } });
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while submitting order', { error: e });
    }
  }

  // TODO-wcn2r2: handle errors
  private async submitGuestOrder() {
    try {
      const order = this.order!;
      const checkout = this.$appStore.state.checkout;
      const info = checkout.guest.info;

      // We want to run all orthogonal promises in parallel
      const promises = [] as Promise<any>[];

      // Get token for credit card info if we don't already have it. Only with PYEZ.
      if (this.$appStore.state.checkout.paymentProvider === PaymentProviders.Payeezy && this.willPayAboveStore && !this.isStubPayment) {
        promises.push(this.useOneTimePaymentFormAsPaymentMethod());
      }

      // Patch user profile with guest info form data if we haven't already patched the user
      if (checkout.guest.needsProfilePatch) {
        promises.push(
          (async () => {
            await this.$services.customer.updateCustomer({
              firstName: info.customerName.value,
              email: info.email.value,
              phoneNumber: info.phoneNumber.value,
            });

            checkout.guest.needsProfilePatch = false;

            await this.$services.customer.refreshCustomerProfileInfo();
          })(),
        );
      }

      // Wait for all the previous work to successfully complete
      await Promise.all(promises);

      // Grab the order id (since we're about to lose it after we reset the orders)
      const orderId = order.order.orderId;

      try {
        await this.submitOrder();
      } catch (e: any) {
        // Throw error if payment is native wallet in order to complete wallet session with a status.
        if (checkout.payment.methods.some(method =>
          method.paymentType === SubmitOrderRequestPaymentMethodType.ApplePay
          || method.paymentType === SubmitOrderRequestPaymentMethodType.GooglePay
        )) {
          throw e;
        }
        return;
      }

      // Reset checkout state
      this.$services.order.resetCurrentOrder();
      this.$services.checkout.reset();

      // Take the customer to the confirmation screen
      this.$router.replace({ name: Routes.OrderConfirmation, params: { orderId } });
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while submitting order', { error: e });
    }
  }

  private async submitOrder(): Promise<void> {
    const seonSessionPromise = this.$services.seonService.getSession();

    // Add delivery info if appropriate
    let deliveryInfoRequest = null;
    if (!this.isPickup && this.address) {
      deliveryInfoRequest = await this.updateOrdersDeliveryInfo(this.order!, this.address);
    }

    const request = this.getSubmitOrderRequest(await seonSessionPromise);
    const order = this.order!;

    try {
      // Submit the order!
      await this.$services.order.submitOrder(order, request);
      await this.afterSubmitOrder(order);
    } catch (e: any) {
      //reset deliveryEstimate without restaurantId
      const submitErrorCode = e instanceof OrderApiException
        ? e.errors?.[0]?.errorCode
        : e.response?.data?.errorCode;
      //handle error gracefully by resetting delivery info and calling submit order again
      if(submitErrorCode === OrderErrorCode.RestaurantIdDoesNotMatchEstimateId) {
        await this.$services.checkout.refreshDeliveryInfo(true); //call with flag so we get estimates stored with newly returned restaurantId
        if(this.$appStore.state.checkout.delivery.estimate?.restaurantId){
          await this.$services.order.changeRestaurantForOrder(order, this.$appStore.state.checkout.delivery.estimate?.restaurantId, false)
          try {
            await this.$services.order.submitOrder(order, request);
            await this.afterSubmitOrder(order);
            this.$router.replace({ name: Routes.OrderConfirmation, params: { orderId: order.order.orderId } });
          } catch (e: any) {
            console.log('error on 1158 re-submit', e);

            if (e instanceof OrderApiException) { 
              // display error modal if restaurant timeslot is unavailable
              if (e.errors?.[0]?.errorCode === OrderErrorCode.UnableToReservePickupTime) { 
                this.onOrderSubmissionError(e, {
                  pickupDateTime: request.pickupDateTime,
                  deliveryPhoneNumber: (deliveryInfoRequest && deliveryInfoRequest.dropOffContactPhoneNumber) || '',
                  order,
                });
              }
            }
          }
        }
      }

      if(submitErrorCode !== OrderErrorCode.RestaurantIdDoesNotMatchEstimateId) {
        try {
          this.onOrderSubmissionError(e, {
              pickupDateTime: request.pickupDateTime,
              deliveryPhoneNumber: (deliveryInfoRequest && deliveryInfoRequest.dropOffContactPhoneNumber) || '',
              order,
            });
        } catch (e: any) {
          this.$services.ui.setModalVisibility('globalErrorToast', true);
        }
      }

      throw e;
    } finally {
      // clear out the transactionId if a wallet card
      const paymentMethod = this.$appStore.state.checkout.payment.methods;
      if (paymentMethod && paymentMethod[0] && paymentMethod[0].paymentType === SubmitOrderRequestPaymentMethodType.ChipotleWalletCreditCard) {
        paymentMethod[0].transactionId = undefined;
        this.$services.checkout.setPaymentMethods(paymentMethod);
      }
    }
  }

  private async afterSubmitOrder(order: OrderWithContext) {
    const estimate = this.deliveryEstimate ? { ...this.deliveryEstimate } : null;
    const bagRewards = this.$appStore.state.reward?.bagRewards ?? [];
    const appliedRewards = this.$appStore.state.reward?.appliedRewards ?? [];
    const rewardCandidates = [...bagRewards, ...appliedRewards];
    const redeemedRewards = rewardCandidates.length > 0
      ? rewardCandidates
      : undefined;
    const updatedOrder = await this.$services.order.getOrder(order.order.orderId, true);
    if (updatedOrder) {
      this.$services.order.setLastOrder(updatedOrder, estimate);
    }

    if (this.isEnrolledInLoyalty) {
      this.$services.customer.setJoinedLoyaltyOnOrderConfirmationPage(false);
    }

    if (updatedOrder) {
      this.$services.branch.sendOrderSubmitSuccessEvent();
      if (this.customer) {
        this.$services.googleAnalytics.sendEcommerceTransactionEvent(updatedOrder, this.customer, this.isEnrolledInLoyalty || false);
      }
      if(this.isUpdatedContextualRewardsBagUpdatesEnabled){
        this.$services.analytics.send(new SubmitOrderEvent(updatedOrder, this.isCustomTip, this.$route.name, redeemedRewards));
      } else {
        this.$services.analytics.send(new SubmitOrderEvent(updatedOrder, this.isCustomTip, this.$route.name));
      }
      this.$services.analytics.clearProductAddMethodStore();
    }
  }

  private async onOrderSubmissionError(e: any, request: RequestData) {
    if (!(e instanceof OrderApiException)) {
      const axiosError = e as AxiosError;

      // If the service 500s, assume we did the worst thing possible
      if (axiosError.response && axiosError.response.status && axiosError.response.status === 500) {
        this.$services.ui.setModalVisibility('basicError', true, {
          header: `YOUR ORDER WASN'T SUBMITTED.`,
          content: 'Please try again.',
          options: [
            {
              text: 'Okay',
              type: ButtonType.Secondary,
              onClick: () => this.$services.ui.setModalVisibility('basicError', false),
            },
          ],
        });
        return;
      }
      throw e;
    }

    const error = e.errors[0];
    if (!error) {
      throw e;
    }

    if (error.errorCode === OrderErrorCode.OrderCompleted) {
      return this.$router.replace({ name: Routes.OrderConfirmation, params: { orderId: request.order.order.orderId } });
    }

    if (!this.isPickup && error.errorCode === OrderErrorCode.DeliveryFailure) {
      const errorText = `There was an error processing your order. Your card hasn't been charged. Please try again.`;
      this.handleDeliveryError(errorText);
      return;
    }

    if (!this.isPickup && (error.errorCode === OrderErrorCode.PurchaseNotApproved || error.errorCode === OrderErrorCode.PurchaseNotCompleted)) {
      const errorText = 'Payment failed. Check your payment method, or add a new payment method and retry.';
      this.handleDeliveryError(errorText);
      return;
    }

    if (error.errorCode === OrderErrorCode.PaymentFailedWithDeliveryCancellationFailed || error.errorCode === OrderErrorCode.PaymentNotApprovedWithDeliveryCancellationFailed) {
      this.isProcessingError = true;
      this.processingErrorText = 'Payment failed. Check your payment method, or add a new payment method and retry.';
    }

    // if there was a crew tip in the submission but the restaurant no longer allows it, remove the crew tip and resubmit the order
    if (error.errorCode === OrderErrorCode.CrewTippingUnavailable && this.order) {
      this.isRemovingCrewTip = true;
      try {
        return await this.$services.order.updateOrderCrewTip(this.order, 0).then(() => {
          this.isRemovingCrewTip = false;
          this.onSubmitOrder();
        });
      } catch {
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      }
    }

    const pickupContainer = this.$refs['pickupContainer'] as HTMLElement;
    await displayOrderSubmitErrorModal(
      error.errorCode,
      request,
      this.showPaymentIssueModal,
      this.minimumOrderTotalForDelivery,
      this.isPickup,
      this.$appStore,
      this.$services,
      pickupContainer,
    );
  }

  private async useOneTimePaymentFormAsPaymentMethod() {
    const providerId = this.$appStore.state.checkout.paymentProvider;
    const payment = this.$appStore.state.checkout.payment;
    const isCardSaved = this.$appStore.state.checkout.payment.isCardSaved;

    // If remember card and card has been just now saved, don't attempt retokenization and saving card again
    if (payment.rememberPaymentMethod.value && isCardSaved) {
      return;
    }

    const paymentForm = this.$appStore.state.checkout.payment.paymentForm;

    if (!paymentForm) {
      throw new Error('Error: Invalid payment form.');
    }

    // 1. Authenticate the current token.
    const token = await this.$services.transaction.getSessionToken(providerId);

    // 2. Submit the form to Payeezy to tokanize it
    const clientToken = await this.$services.transaction.tokenizePayeezyCard(token, paymentForm!);

    try {
      // 3. Get the token from TransactionV3
      const tokenizedCardResponse = await this.$services.transaction.getTransactionToken(providerId, clientToken);

      const tokenizedCard = {
        cardHolderName: tokenizedCardResponse.cardHolderName,
        expiration: tokenizedCardResponse.expiration,
        paymentMethod: tokenizedCardResponse.paymentMethod,
        tokenizedAccountNumber: tokenizedCardResponse.paymentToken,
        paymentProviderId: providerId,
        lastFourAccountNumbers: tokenizedCardResponse.last4,
        billingZip: tokenizedCardResponse.billingZip,
      } as TokenizedPaymentCard;

      if (payment.rememberPaymentMethod.value) {
        try {
          // 4. Save the payment card
          await this.$services.transaction.savePaymentCard(tokenizedCard);
          // Saved card successfully, so hold that in state so we don't re-save card.
          this.$services.checkout.setIsCardSaved(true);
        } catch (e: any) {
          // TODO-ayn4yx: Tell the user that we just couldn't save the card
          // Intentionally swallowing the error with no feedback to the user as to not disrupt checkout process
          // when saving a card fails.
          this.$services.logger.error('useOneTimePaymentFormAsPaymentMethod: Failed to save card ', e);
        }
      }

      this.$services.checkout.setPaymentMethods([
        {
          cardHolderName: tokenizedCardResponse.cardHolderName!,
          creditCardSingleUseToken: tokenizedCardResponse.paymentToken!,
          creditCardType: tokenizedCardResponse.paymentMethod!.replace(/-/g, ' '),
          creditCardExpiration: tokenizedCardResponse.expiration,
          creditCardZipCode: tokenizedCard.billingZip,
          paymentType: SubmitOrderRequestPaymentMethodType.CreditCard,
          paymentProviderId: providerId,
          lastFourAccountNumbers: tokenizedCardResponse.last4,
        },
      ]);
    } catch (e: any) {
      this.$services.ui.setModalVisibility('basicError', true, {
        header: 'Oops!',
        content: 'Sorry, there was an error processing payment.',
        options: [
          {
            text: 'Ok',
            type: ButtonType.Primary,
            onClick: () => this.$services.ui.setModalVisibility('basicError', false),
          },
        ],
      });

      throw e;
    }
  }

  private async getProvider() {
    if (!this.order) {
      throw new Error('Order does not exist at checkout');
    } else {
      try {
        let providers;
        if (!this.$appStore.state.customer.profile) {
          providers = await this.$services.transaction.getProvidersByCountry(this.countryCode);
        } else {
          providers = await this.$services.transaction.getProviders(this.order.order.restaurantId.toString());
        }

        if (providers && providers.length > 0 && providers[0]) {
          const providerId = providers[0].paymentProviderId;
          this.$services.checkout.setPaymentProvider(providerId);
        } else {
          this.showPaymentIssueModal(this.order);
        }
      } catch (error: any) {
        this.$services.logger.error('getProvider: Failed to get provider at checkout', error);
      } finally {
        this.isFetchingPaymentProvider = false;
      }
    }
  }

  private getCrossBorderMessage(countryCode: string): string {
    switch (countryCode) {
      case CountryCode.US:
        return 'Viewing U.S. Payment Methods';
      case CountryCode.CA:
        return 'Viewing Canadian Payment Methods';
      default:
        return `Viewing ${countryCode} Payment Methods`;
    }
  }

  private async validateOrderState() {
    if (!this.order) {
      return;
    }

    if (this.restaurant && this.order.order.restaurantId !== this.restaurant.restaurantNumber) {
      // If the selected restaurant does not match with the restaurant on the order,
      // we want to set the restaurant selected to the order
      await this.$services.restaurant.selectRestaurant(this.restaurant, true);
      await this.$services.order.refreshCurrentOrder();
      return;
    }
  }

  private async updateOrdersDeliveryInfo(order: OrderWithContext, deliveryAddress: Address): Promise<UpdateDeliveryInfoOnOrderRequest | undefined> {
    try {
      const profile = this.$appStore.state.customer.profile;
      const guestInfo = this.$appStore.state.checkout.guest.info;

      const deliveryPhone = this.$appStore.state.checkout.delivery.phoneNumber || guestInfo.phoneNumber.value || (profile && profile.phoneNumber);
      const deliveryName = this.$appStore.state.checkout.delivery.name || guestInfo.customerName.value || (profile && profile.firstName);

      const request = {
        dropOffAddressLine1: deliveryAddress.line1 || '',
        dropOffAddressLine2: deliveryAddress.line2 || '',
        dropOffAddressCity: deliveryAddress.locality || '',
        dropOffAddressState: deliveryAddress.administrativeArea || '',
        dropOffAddressZipCode: deliveryAddress.postalCode || '',
        dropOffAddressCountryCode: deliveryAddress.countryCode || '',
        dropOffContactFirstName: deliveryName || '',
        dropOffContactLastName: '',
        dropOffContactPhoneNumber: deliveryPhone || '',
        dropOffInstructions: this.$appStore.state.checkout.delivery.instructions.value,
        dropOffLatitude: deliveryAddress.latitude,
        dropOffLongitude: deliveryAddress.longitude,
        isContactlessDelivery: this.$appStore.state.order.selectedDeliveryAddress?.contactlessDropoff
      };

      if (order.order.delivery) {
        await this.$services.order.updateDeliveryDropoffInfoOnOrder(order, request);
      } else {
        await this.$services.order.addDeliveryInfoToOrder(order, request);
      }

      return request;
    } catch (e: any) {
      const isOrderApiException = e instanceof OrderApiException;
      const isOrderAlreadyCompleted = isOrderApiException && e.errors[0] && e.errors[0].errorCode === OrderErrorCode.OrderCompleted;

      this.$services.logger.error('updateOrdersDeliveryInfo: Failed attaching delivery info ', e);
      this.$services.ui.setModalVisibility('globalErrorToast', !isOrderAlreadyCompleted);
    }
  }

  private scrollToFirstError() {
    // HACK: this seems dirty but it may also be the most effective way to scroll something into view
    const container = this.$refs['container'] as HTMLElement | null;
    const content = document.querySelector(`#${ui.componentIds.cmgAppContent}`);

    if (!container || !content) {
      return;
    }

    // HACK: take it as convention that any section that has an error has the has-error class
    const firstError = container.querySelector('.has-error') as HTMLElement | null;
    if (!firstError) {
      return;
    }

    // Account for the rough header height when scrolling to element
    content.scrollTop = firstError.offsetTop - 100;
  }

  private showPaymentIssueModal(order: OrderWithContext) {
    const text = order.discounts?.length
      ? `But you can pay at the restaurant. This will remove your offer. You can still use it at pickup today or it'll be saved for later. You'll pay **** plus tax at pickup`
      : this.$services.order.isDelivery()
        ? 'If you want to continue, try again or switch to pickup and pay in the restaurant.'
        : 'But you can pay at the restaurant. You\'ll pay **** plus tax at pickup';
    this.$services.analytics.send(new ErrorEvent('checkout-payment', `We're experiencing an issue processing payments. ${text}`, 'checkout-payment-issue'));

    this.$services.ui.setModalVisibility('checkoutPaymentIssue', true, {
      order,
      isDelivery: !this.isPickup,
      onTryAgain: async () => {
        this.$services.ui.setModalVisibility('checkoutPaymentIssue', false);
        this.onSubmitOrder();
      },
      onClickSwitchToPayInStore: async () => {
        this.$services.ui.setModalVisibility('checkoutPaymentIssue', false);
        await this.removePromotionsFromOrder();
        this.$services.order.switchToPickup();
        this.$services.checkout.forceInStorePayment(true);
        this.onSubmitOrder();
      },
    });
    this.$services.ui.firstFocus();
  }

  private showPaymentAuthorizationModal(paymentMethod: SubmitOrderRequestPaymentMethod, order: Order) {
    this.$services.ui.setModalVisibility('checkoutAuthorizePaymentCard', true, {
      paymentCard: paymentMethod,
      order: order,
      onApprove: async () => {
        this.onSubmitOrder();
      }
    });
  }

  private showDeliveryOfflineModal() {
    this.$services.ui.setModalVisibility('deliveryOfflineModal', true, {
      onClose: () => false,
      onSwitchToPickup: async () => {
        this.$services.ui.setModalVisibility('deliveryOfflineModal', false);
        this.$services.order.switchToPickup();
      },
    });
  }

  private async removePromotionsFromOrder() {
    if (!this.order) {
      throw new Error('Order does not exist at checkout');
    }

    // Remove any discounts on the order (since they can't be applied to in-store payments)
    for (const discount of this.order.discounts || []) {
      await this.$services.order.removeDiscountFromOrder(this.order, discount.promotionCode);
      this.promotionRemoved = true;
    }
  }

  private getSubmitOrderRequest(seonSession: string | undefined) {
    const { checkout, customer } = this.$appStore.state;
    const { profile } = customer;
    const personalizationAttributes: { [index: string]: string } = {};
    let bagPickupLocationId = this.bagPickupLocation;

    if (!this.willPayAboveStore) {
      bagPickupLocationId = BagPickupLocation.HoldForPayment;
    } else if (this.isPickup && bagPickupLocationId === BagPickupLocation.Shelf && profile?.bottomShelfPreference) {
      bagPickupLocationId = BagPickupLocation.LowerShelf;
    }

    // Only pass the ECID if it's been received from Target
    if (this.$appStore.state.adobeTarget.ecid) {
      personalizationAttributes.ECID = this.$appStore.state.adobeTarget.ecid;
    }
    // Only pass the region if it's been received from Target
    if (this.$appStore.state.adobeTarget.region) {
      personalizationAttributes.region = this.$appStore.state.adobeTarget.region;
    }
    return {
      isAboveStorePayment: this.willPayAboveStore,
      payments: this.$appStore.state.checkout.payment.methods,
      bagPickupLocationId,

      // We always need to provide a pickup time (even if the order is delivery) -- think of it more like "order ready time"
      pickupDateTime: this.isPickup ? checkout.pickup.pickupTime.value : this.$appStore.state.checkout.delivery.estimate?.pickupTime ?? '',
      deliveryEstimateId: this.$appStore.state.checkout.delivery.estimate?.estimateId,
      personalization: personalizationAttributes,
      seonSession
    };
  }

  private async handleDeliveryError(errorText: string) {
    await this.$services.checkout.refreshDeliveryInfo();
    const deliveryEstimate = this.$appStore.state.checkout.delivery.estimate;

    let deliveryEstimateMessage = 'Your estimated arrival time has changed'; // TODO: What do we display when we can get an estimate
    deliveryEstimateMessage += deliveryEstimate ? ` to ${deliveryEstimate.minDeliveryTime} - ${deliveryEstimate.maxDeliveryTime} minutes.` : '.';

    this.isProcessingError = true;
    this.processingErrorText = errorText;
    setTimeout(() => {
      this.$services.ui.setModalVisibility('toast', true, {
        body: deliveryEstimateMessage,
        bodyOnly: true,
        isUpdatedVersion: true,
        visibleTimeLength: 7000,
        unmaskContent: true,
      });
    }, 1000);
  }

  private subscribeToIsOrderConfirmed() {
    if (this.unsubscribe) {
      return;
    }

    this.unsubscribe = this.$appStore.subscribe((mutation, state) => {
      if (mutation.type === 'checkout/isOrderConfirmed') {
        if (state.checkout.isOrderConfirmed === true) {
          if (this.interstitialResolver) {
            this.interstitialResolver(true);
            this.interstitialResolver = null;
          }
        } else if (state.checkout.isOrderConfirmed === false) {
          if (this.interstitialResolver) {
            this.interstitialResolver(false);
            this.interstitialResolver = null;
          }
        }
      } 
    });
  }

  private async refreshTimeEstimates(order: OrderWithContext) {
    if (this.isPickup) {
      this.$services.checkout.setAvailablePickupTimes(await this.$services.sput.getPickupTimesForRestaurant(order.order.restaurantId, getEntreeCount(order.order)));
    } else {
      const estimate = this.$appStore.state.checkout.delivery.estimate;
      const order = this.$services.order.getCurrentOrder();
  
      if (order != null && order.order.delivery != null && estimate != null) {
        this.$services.checkout.setDeliveryEstimate({
          ...estimate,
          deliveryFee: order.order.delivery.feeAmount,
        });
      }
    }
  }
}
